/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type MemoExoticComponent } from 'react';

import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';

import type { JiraMultipleSelectUserPickerField_Platform$key } from './__generated__/JiraMultipleSelectUserPickerField_Platform.graphql';
import {
	type UserOption,
	UserPickerComponent,
	type UserPickerComponentProps,
} from './UserPickerComponent';

export const JiraMultipleSelectUserPickerField: MemoExoticComponent<
	({
		field,
		isFieldEditable,
		...props
	}: { field: JiraMultipleSelectUserPickerField_Platform$key } & Omit<
		UserPickerComponentProps,
		'isMulti' | 'defaultUsers' | 'isRequired' | 'isEditable'
	>) => JSX.Element
> = memo(
	({
		field,
		isFieldEditable = true,
		...props
	}: { field: JiraMultipleSelectUserPickerField_Platform$key } & Omit<
		UserPickerComponentProps,
		'isMulti' | 'defaultUsers' | 'isRequired' | 'isEditable'
	>): JSX.Element => {
		const data = useFragment<JiraMultipleSelectUserPickerField_Platform$key>(
			graphql`
				fragment JiraMultipleSelectUserPickerField_Platform on JiraMultipleSelectUserPickerField {
					fieldConfig {
						isEditable
						isRequired
					}
					selectedUsersConnection {
						edges {
							node {
								id
								name
								accountId
								picture
							}
						}
					}
				}
			`,
			field,
		);

		const isEditable = (data?.fieldConfig?.isEditable ?? false) && isFieldEditable;
		const isRequired = data?.fieldConfig?.isRequired ?? false;

		const defaultUsers = data?.selectedUsersConnection?.edges
			?.map(
				(edge) =>
					edge?.node?.id &&
					edge.node.accountId && {
						id: edge.node.id,
						accountId: edge.node.accountId,
						name: edge.node.name,
						avatarUrl: edge.node.picture ?? undefined,
					},
			)
			.filter(Boolean) as UserOption[] | undefined;

		return (
			<UserPickerComponent
				{...props}
				isEditable={isEditable}
				isRequired={isRequired}
				defaultUsers={defaultUsers ?? null}
				isMulti
			/>
		);
	},
);
